import { Link, useParams } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import ArticleCard from "@/components/feature/ArticleCard";
import { articles } from "@/mocks/articles";
import { categories } from "@/mocks/categories";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  if (!id) {
    return <ArticleList />;
  }

  const article = articles.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <>
        <Seo title="文章未找到 - 动漫网" description="动漫网，你访问的文章不存在或已被移除。" canonical="/article" />
        <section className="flex min-h-[60vh] flex-col items-center justify-center px-4 pt-24 text-center">
          <i className="ri-file-warning-line text-5xl text-foreground-300"></i>
          <h1 className="mt-4 text-xl font-semibold text-foreground-900">文章不存在或已被移除</h1>
          <p className="mt-2 text-sm text-foreground-600">去看看其他精彩内容吧</p>
          <Link to="/article" className="mt-5 inline-flex items-center whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition hover:bg-primary-600">
            返回资讯列表
          </Link>
        </section>
      </>
    );
  }

  const categoryName = categories.find((c) => c.slug === article.categorySlug)?.name || article.category;
  const related = articles.filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id).slice(0, 3);
  const fallbackRelated = related.length < 3 ? articles.filter((a) => a.id !== article.id).slice(0, 3 - related.length) : [];

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.title,
      description: article.summary,
      image: [article.cover],
      datePublished: article.date,
      dateModified: article.date,
      author: { "@type": "Person", name: article.author },
      publisher: { "@type": "Organization", name: "动漫网" },
      keywords: article.tags.join(","),
      mainEntityOfPage: `https://animelist8.github.io/article/${article.id}`,
      articleSection: categoryName,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "动漫网", item: "https://animelist8.github.io/" },
        { "@type": "ListItem", position: 2, name: "动漫资讯", item: "https://animelist8.github.io/article" },
        { "@type": "ListItem", position: 3, name: categoryName },
        { "@type": "ListItem", position: 4, name: article.title },
      ],
    },
  ];

  return (
    <>
      <Seo title={`${article.title} - 动漫网`} description={article.summary} keywords={article.tags.join(",")} canonical={`/article/${article.id}`} type="article" jsonLd={jsonLd} />

      <section className="bg-background-100 pt-24 md:pt-28">
        <div className="mx-auto max-w-4xl px-4 md:px-6 py-8 md:py-10">
          <Breadcrumbs
            items={[
              { label: "动漫网", to: "/" },
              { label: "动漫资讯", to: "/article" },
              { label: categoryName, to: `/category/${article.categorySlug}` },
              { label: article.title },
            ]}
          />
          <h1 className="mt-5 font-heading text-2xl leading-snug text-foreground-900 md:text-4xl md:leading-snug">{article.title}</h1>
          <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">{article.summary}</p>
          <div className="mt-5 flex flex-wrap items-center gap-4 border-y border-background-200 py-4 text-xs text-foreground-500 md:text-sm">
            <span className="flex items-center gap-1.5"><i className="ri-user-line"></i>{article.author}</span>
            <span className="flex items-center gap-1.5"><i className="ri-calendar-line"></i>{article.date}</span>
            <span className="flex items-center gap-1.5"><i className="ri-eye-line"></i>{article.views.toLocaleString()} 次阅读</span>
            <Link to={`/category/${article.categorySlug}`} className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-primary-100 px-3 py-1 font-medium text-primary-700 transition hover:bg-primary-200">
              <i className="ri-price-tag-3-line text-xs"></i>{categoryName}
            </Link>
          </div>
        </div>
      </section>

      <article className="py-10 md:py-14">
        <div className="mx-auto max-w-4xl px-4 md:px-6">
          <div className="overflow-hidden rounded-lg">
            <img src={article.cover} alt={`${article.title} - 动漫网`} title={`${article.title} - 动漫网`} className="aspect-[16/9] w-full object-cover" />
          </div>
          <div className="mt-8 space-y-8 md:mt-10">
            {article.content.map((block, i) => (
              <section key={i}>
                {block.heading && <h2 className="text-xl font-bold text-foreground-900 md:text-2xl">{block.heading}</h2>}
                <div className="mt-3 space-y-4">
                  {block.paragraphs.map((p, j) => (
                    <p key={j} className="text-sm leading-7 text-foreground-700 md:text-base md:leading-8">{p}</p>
                  ))}
                </div>
              </section>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap gap-2">
            {article.tags.map((t) => (
              <Link key={t} to={`/category/all?q=${encodeURIComponent(t)}`} className="inline-flex items-center whitespace-nowrap rounded-full bg-background-100 px-3.5 py-1.5 text-xs font-medium text-foreground-600 transition hover:bg-secondary-100 hover:text-secondary-700">
                #{t}
              </Link>
            ))}
          </div>

          <div className="mt-8 flex items-center justify-between border-t border-background-200 pt-6">
            <Link to="/article" className="inline-flex items-center whitespace-nowrap text-sm font-medium text-primary-600 transition hover:text-primary-700">
              <i className="ri-arrow-left-line mr-1.5"></i> 返回资讯列表
            </Link>
            <div className="flex items-center gap-2 text-sm text-foreground-500">
              <span>分享：</span>
              {["ri-wechat-fill", "ri-weibo-fill", "ri-qq-fill"].map((icon) => (
                <a key={icon} href="#" aria-label="分享" className="flex h-8 w-8 items-center justify-center rounded-full bg-background-100 text-foreground-600 transition hover:bg-primary-100 hover:text-primary-600">
                  <i className={`${icon} text-base`}></i>
                </a>
              ))}
            </div>
          </div>
        </div>
      </article>

      <section className="bg-background-100 py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <h2 className="font-heading text-xl text-foreground-900 md:text-2xl">相关推荐</h2>
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {[...related, ...fallbackRelated].map((a) => (
              <ArticleCard key={a.id} article={a} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

function ArticleList() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "动漫资讯 - 动漫网",
    url: "https://animelist8.github.io/article",
    description: "动漫网最新动漫资讯，聚合新番前瞻、经典盘点、深度评测等精彩内容。",
    inLanguage: "zh-CN",
  };

  return (
    <>
      <Seo
        title="动漫资讯 - 动漫网"
        description="动漫网最新动漫资讯，聚合新番前瞻、经典盘点、深度评测等精彩内容，持续更新。"
        keywords="动漫资讯,新番推荐,动漫评测,动漫盘点"
        canonical="/article"
        type="collection"
        jsonLd={[jsonLd]}
      />
      <section className="bg-background-100 pt-24 md:pt-28">
        <div className="mx-auto max-w-7xl px-4 md:px-6 py-8 md:py-10">
          <Breadcrumbs items={[{ label: "动漫网", to: "/" }, { label: "动漫资讯" }]} />
          <div className="mt-4">
            <h1 className="font-heading text-2xl text-foreground-900 md:text-4xl">动漫资讯</h1>
            <p className="mt-3 max-w-2xl text-sm text-foreground-600 md:text-base">新番前瞻、经典盘点、深度评测，每天更新精彩内容</p>
          </div>
        </div>
      </section>
      <section className="py-10 md:py-14">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {articles.map((a) => (
              <ArticleCard key={a.id} article={a} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}